import { useRef, useState } from "react";
import { Upload, Link2, Loader2, X } from "lucide-react";
import api from "../../lib/api";

export default function ImageUploader({ value, onChange, label = "Image", testid = "image-uploader" }) {
  const fileRef = useRef(null);
  const [mode, setMode] = useState("upload"); // 'upload' | 'url'
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Erreur lors de l'envoi");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div data-testid={testid}>
      <label className="overline block mb-2">{label}</label>

      <div className="flex gap-1 mb-3 bg-[#F6F3EC] rounded-xl p-1 w-fit">
        <button
          type="button"
          onClick={() => setMode("upload")}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition ${mode === "upload" ? "bg-[#2D2A26] text-white" : "text-[#4A3F35]"}`}
          data-testid={`${testid}-mode-upload`}
        >
          <Upload size={12} /> Envoyer
        </button>
        <button
          type="button"
          onClick={() => setMode("url")}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition ${mode === "url" ? "bg-[#2D2A26] text-white" : "text-[#4A3F35]"}`}
          data-testid={`${testid}-mode-url`}
        >
          <Link2 size={12} /> URL
        </button>
      </div>

      {mode === "upload" ? (
        <div>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif,image/heic"
            onChange={handleFile}
            className="hidden"
            data-testid={`${testid}-file-input`}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="w-full border-2 border-dashed border-[#2D2A26]/20 hover:border-[#C87A5E] rounded-xl py-5 px-4 flex flex-col items-center gap-2 transition disabled:opacity-50"
            data-testid={`${testid}-pick`}
          >
            {uploading ? (
              <><Loader2 size={20} className="animate-spin text-[#C87A5E]" /><span className="text-sm">Envoi en cours…</span></>
            ) : (
              <><Upload size={20} className="text-[#C87A5E]" /><span className="text-sm">Cliquez pour choisir une image</span><span className="text-xs text-[#4A3F35]/50">JPG, PNG, WebP — max 10 Mo</span></>
            )}
          </button>
        </div>
      ) : (
        <input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://…"
          className="w-full border border-[#2D2A26]/15 rounded-xl px-4 py-2.5 text-sm focus:border-[#C87A5E] outline-none"
          data-testid={`${testid}-url-input`}
        />
      )}

      {error && <div className="text-xs text-red-700 mt-2" data-testid={`${testid}-error`}>{error}</div>}

      {value && (
        <div className="mt-3 relative inline-block">
          <img src={value} alt="preview" className="rounded-xl max-h-40 object-cover" data-testid={`${testid}-preview`} />
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute -top-2 -right-2 w-7 h-7 bg-white rounded-full shadow-md border border-[#2D2A26]/10 flex items-center justify-center hover:bg-red-50 hover:text-red-700 transition"
            title="Retirer"
            data-testid={`${testid}-clear`}
          >
            <X size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
